import { useState, useEffect, useRef } from 'react'
import { Formik } from 'formik'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import ScheduleService from 'services/Schedule'
import CheckBox from 'components/CheckBox'
import Select from 'components/Inputs/Select'
import MoneyInput from 'components/Inputs/MoneyInput'
import NumberInput from 'components/Inputs/NumberInput'
import DateInput from 'components/Inputs/DateInput'
import MainButton from 'components/Buttons/MainButton'
import editScheduleValidator from 'validators/editScheduleValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { DateTime } from 'luxon'

import {
  Form,
  FormField,
  CheckRow,
  Input,
  TextArea,
  Label,
  Row,
  InfoText,
  WarningText,
  ErrorContainer
} from './styles'

export default function EditScheduleForm ({
  data: schedule,
  workingDays = [],
  onEdit: handleEdit
}) {
  const { 
    id: schedule_id, member, customer, professional, observation,
    service, date, time, duration, price, cutilei_schedule
  } = schedule

  const [loading, setLoading] = useState (true)
  const [errorMessage, setErrorMessage] = useState (null)
  const [professionals, setProfessionals] = useState ([])
  const [services, setServices] = useState ([])
  const [timeWindows, setTimeWindows] = useState ([])
  const token = AuthService.getToken ()
  const isAdmin = AuthService.isAdmin ()
  const businessId = AuthService.getBusinessId ()
  const hasEditPermission = AuthService.hasPermission ('agenda', 'edit')
  const scheduleDateTime = DateTime.fromFormat (`${date} ${time}`, 'yyyy-MM-dd H:mm')
  const isWithinServiceDuration = DateTime.now () < scheduleDateTime?.plus ({minutes: duration})
  const isNailPolishService = service.name.toLowerCase ().includes ('esmaltação')
  const businessFilter = `business_id=${businessId}`

  /*
    Em agendamento Cutilei, só admin pode mudar o horário. Com o horário travado, o
    profissional precisa estar livre exatamente nele; com ele livre, o profissional se escolhe
    primeiro e o horário depois, entre os livres dele. É a mesma condição que trava o campo.
  */
  const isTimeLocked = !!cutilei_schedule && !isAdmin

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  // Cliques seguidos na duração disparam vários pedidos; só a resposta do último vale
  const latestTimeWindowsRequest = useRef(0)

  useEffect (() => {
    getData (schedule)
  }, [schedule])

  /*
    A duração vai junto porque a lista confere conflito com ela: sem isso a API usava a
    configurada do serviço, e um atendimento encurtado para caber num vão nunca achava o vão.
    No Cutilei não vai — a API grava a duração configurada do profissional, não a do pedido.
  */
  const getTimeWindows = async ({ date, service, professional, duration }) => {
    const request = ++latestTimeWindowsRequest.current
    const durationFilter = !cutilei_schedule && duration > 0 ? `&duration=${duration}` : ''

    const { data: timeWindows } = await cutileiApi.get(
      `/professionals/${professional.id}/free_schedules?date=${date}`
      + `&${businessFilter}&schedule_id=${schedule_id}&service_id=${service.id}${durationFilter}`,
      requestConfig
    )

    if (request !== latestTimeWindowsRequest.current) return null

    setTimeWindows(timeWindows.map(tw => ({ label: tw, value: tw })))
    return timeWindows
  }

  const getData = async schedule => {
    try {
      const filters = `?status=active&deleted=false&access_level=service&schedule_date=${schedule.date}`
                    + (isTimeLocked ? `&schedule_time=${schedule.time}` : '')
                    + `&schedule_id=${schedule_id}&service_id=${schedule.service.id}`
                    + (cutilei_schedule ? `&category_name=${schedule.service.category.name}` : '')

      const { data: professionals } = await cutileiApi.get (
        `/businesses/${businessId}/professionals${filters}`, requestConfig
      )

      let { data: services } = await cutileiApi.get (
        `/businesses/${businessId}/professionals/${schedule.professional.id}/services`
        + `?cutilei_services=${cutilei_schedule}`,
        requestConfig
      )

      const timeWindows = await getTimeWindows(schedule)

      setProfessionals (professionals.map (professional => ({
        label: professional.nickname,
        value: professional.id
      })))

      if (cutilei_schedule && !isAdmin) services = services.filter (s => (
        s.id !== schedule.service.id
        && !s.name.toLowerCase ().includes ('pé e mão')
        && !s.name.toLowerCase ().includes ('esmaltação')
      ))

      setServices (services.map (service => ({
        label: service.name, value: service
      })))

      return timeWindows
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const checkScheduleDuration = schedule => {
    const { date, duration } = schedule
    const time = schedule.time.value

    const selectedWeekday = DateTime.fromISO (date).weekday
    const workingDay = workingDays.find (day => day.iso_number === selectedWeekday)
    const parseTime = time => {
      const format = time.length > 5 ? 'HH:mm:ss' : 'H:mm'
      return DateTime.fromFormat (time, format)
    }

    const closingTime = parseTime (workingDay.closing || workingDay.work_end)
    const startTime = parseTime (time)
    const endTime = startTime.plus ({ minutes: duration })

    if (endTime > closingTime) {
      return 'A duração não deve exceder o horário de fechamento do salão'
    }

    return null
  }
  

  if (loading) return (
    <Loading style={{marginBlock: 20, alignSelf: 'center'}}/>
  )

  return (
    <Formik
      validationSchema={editScheduleValidator}
      initialValues={{
        'customer': {
          label: member?.name || customer?.name,
          value: member?.id || customer.id
        },
        'professional': {
          label: professional.nickname,
          value: professional.id
        },
        'service': {label: service.name, value: service},
        'date': date,
        'time': {label: time, value: time},
        'duration': duration,
        'price': price,
        'observation': observation,
        'addRecurrence': false,
        'recurrence': {
          frequency: ScheduleService.FREQUENCY_OPTIONS[0],
          type: {label: 'Qtd. agendamentos', value: 'scheduleCount'},
          scheduleCount: 1,
          endDate: date,
          skipDates: []
        }
      }}
      onSubmit={async (values, { setSubmitting }) => {
        setErrorMessage (null)
        const { recurrence } = values
        const recurrenceDates = ScheduleService.getRecurrenceDates (recurrence, date)

        const durationError = checkScheduleDuration (values)
        if (durationError) return setErrorMessage (durationError)

        const scheduleBody = {
          ...values,
          businessId,
          time: values.time.value,
          customerId: values.customer.value,
          serviceId: values.service.value.id,
          professionalId: values.professional.value,
          ...values.addRecurrence && { recurrenceDates }
        }

        try {
          if (!cutilei_schedule) {
            await cutileiApi.post ('/schedules/verify_availability', scheduleBody, requestConfig)
          }

          const { data: schedule } = await cutileiApi.put (
            `/schedules/${schedule_id}`, scheduleBody, requestConfig
          )

          setSubmitting (false)
          handleEdit (schedule)
        } catch (error) {
          logApiError (error)
          setErrorMessage (getApiErrorMessage (error))
          setSubmitting (false)
        }
      }}
    >
      {function EditScheduleForm ({
        values,
        errors,
        touched,
        dirty,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        handleChange,
        handleBlur,
        handleSubmit
      }) {
        useEffect (() => {
          if (!dirty) return

          getData({
            date: values.date,
            time: values.time?.value,
            service: values.service.value,
            duration: values.duration,
            professional: { ...values.professional, id: values.professional.value }
          }).then(freeTimes => {
            /*
              O horário que veio do agendamento original só fica se estiver livre para o
              profissional escolhido nesta data. Senão o campo esvazia, e o validador obriga a
              escolher de novo — em vez de enviar um horário que a API vai recusar.
            */
            if (!isTimeLocked && freeTimes && values.time && !freeTimes.includes(values.time.value)) {
              setFieldValue('time', null)
            }
          })
        }, [values.professional, values.service, values.date])

        /*
          Mudar a duração refaz a lista, mas não tira o horário escolhido: quem estica um
          atendimento pode querer mesmo ocupar o horário seguinte, e quem encurta precisa ver
          o vão aparecer para então escolher.
        */
        useEffect(() => {
          if (!dirty || cutilei_schedule) return

          getTimeWindows({
            date: values.date,
            service: values.service.value,
            professional: { id: values.professional.value },
            duration: values.duration
          }).catch(logApiError)
        }, [values.duration])

        return (
          <Form onSubmit={handleSubmit}>
            <Row>
              <FormField>
                <Label>Profissional</Label>
                <Select
                  name='professional'
                  placeholder='Profissional...'
                  value={values.professional}
                  options={professionals}
                  onChange={setFieldValue}
                  onBlur={setFieldTouched}
                  error={errors.professional}
                  touched={touched.professional}
                  isDisabled={!hasEditPermission}
                />
                {touched.professional && errors.professional && (
                  <WarningText>
                    {errors.professional}
                  </WarningText>
                )}
              </FormField>
              <FormField>
                <Label>Serviço</Label>
                {!schedule.cutilei_schedule || isAdmin || (
                  schedule.cutilei_schedule && schedule.validated
                  && isWithinServiceDuration && !isNailPolishService
                ) ? (
                  <Select
                    name='service'
                    placeholder='Serviço...'
                    noOptionsMessage='Este profissional ainda não possui serviços cadastrados'
                    value={values.service}
                    options={services}
                    onChange={(name, option) => {
                      setFieldValue ('price', option.value.price)
                      setFieldValue ('duration', option.value.duration)
                      setFieldValue (name, option)
                    }}
                    onBlur={setFieldTouched}
                    error={errors.service}
                    touched={touched.service}
                    isDisabled={!hasEditPermission}
                  />
                ) : (
                  <Input type='text' value={values.service.label} disabled/>
                )}
                {touched.service && errors.service && (
                  <WarningText>
                    {errors.service}
                  </WarningText>
                )}
              </FormField>
            </Row>
            <Row>
              <FormField>
                <Label>Data</Label>
                {schedule.cutilei_schedule ? (
                  <Input
                    type='text'
                    value={DateTime.fromISO (values.date).toFormat ('dd/MM/yyyy')}
                    disabled={true}
                  />
                ) : (
                  <DateInput
                    name='date'
                    placeholder='Data'
                    value={values.date}
                    disabled={!hasEditPermission}
                    onChange={date => setFieldValue ('date', date)}
                    onBlur={setFieldTouched}
                    style={{width: '100%'}}
                  />
                )}
                {touched.date && errors.date && (
                  <WarningText>
                    {errors.date}
                  </WarningText>
                )}
              </FormField>
              <FormField>
                <Label>Horário</Label>
                {isTimeLocked ? (
                  <Input type='text' value={values.time.label} disabled/>
                ) : (
                  <Select
                    name='time'
                    placeholder='Horário...'
                    value={values.time}
                    options={timeWindows}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                    error={errors.time}
                    touched={touched.time}
                    isDisabled={!hasEditPermission}
                  />
                )}
                {!isTimeLocked && timeWindows.length === 0 && (
                  <WarningText>
                    Sem horário livre para este profissional nesta data
                  </WarningText>
                )}
                {touched.time && errors.time && (
                  <WarningText>
                    {errors.time}
                  </WarningText>
                )}
              </FormField>
              {!schedule.cutilei_schedule && (
                <>
                  <FormField>
                    <Label>Duração (mins)</Label>
                    <NumberInput
                      id='duration'
                      name='duration'
                      value={values.duration}
                      minValue={15}
                      stepOnly={true}
                      step={values.duration < 60 ? 15 : 30}
                      onChange={(name, value) => {
                        const newValue = values.duration === 60 && values.duration > value ? 45 : value
                        setFieldValue (name, newValue)
                      }}
                      style={{width: '100%'}}
                    />
                  </FormField>
                  <FormField>
                    <Label>Preço</Label>
                    <MoneyInput
                      name='price'
                      placeholder='Preço'
                      value={values.price}
                      disabled={!hasEditPermission}
                      onValueChange={setFieldValue}
                      onBlur={handleBlur ('price')}
                    />
                  </FormField>
                </>
              )}
            </Row>
            {!schedule.cutilei_schedule && (
              <Row style={{marginBottom: 10}}>
                <FormField>
                  <Label htmlFor='observation'>
                    Observações (opcional)
                  </Label>
                  <TextArea
                    id='observation'
                    placeholder='Observações (opcional)'
                    rows={2}
                    value={values.observation}
                    onChange={handleChange ('observation')} 
                    onBlur={handleBlur ('observation')}
                  />
                  {touched.observation && errors.observation && (
                    <WarningText>
                      {errors.observation}
                    </WarningText>
                  )}
                </FormField>
              </Row>
            )}
            {!schedule.cutilei_schedule && (
              <CheckRow>
                <CheckBox
                  id='addRecurrence'
                  style={{marginRight: -3}}
                  value={values.addRecurrence}
                  disabled={!hasEditPermission}
                  onValueChange={value => setFieldValue ('addRecurrence', value)}
                />
                <Label htmlFor='addRecurrence' style={{color: '#000000'}}>
                  Adicionar recorrência
                </Label>
              </CheckRow>
            )}
            {values.addRecurrence && (
              <Row>
                <FormField>
                  <Label>Frequência</Label>
                  <Select
                    name='recurrence.frequency'
                    placeholder='Recorrência...'
                    value={values.recurrence.frequency}
                    options={ScheduleService.FREQUENCY_OPTIONS}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                    containerStyles={{width: 220}}
                  />
                </FormField>
                <FormField>
                  <Label>Config. duração</Label>
                  <Select
                    name='recurrence.type'
                    placeholder='Tipo...'
                    value={values.recurrence.type}
                    options={[
                      {label: 'Qtd. agendamentos', value: 'scheduleCount'},
                      {label: 'Data final', value: 'endDate'}
                    ]}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                    containerStyles={{width: 230}}
                  />
                </FormField>
                {values.recurrence.type?.value === 'scheduleCount' && (
                  <FormField>
                    <Label>Qtd. agendamentos</Label>
                    <NumberInput
                      name='recurrence.scheduleCount'
                      placeholder='Qtd. agendamentos'
                      value={values.recurrence.scheduleCount}
                      minValue={1}
                      maxValue={99}
                      onChange={setFieldValue}
                    />
                  </FormField>
                )}
                {values.recurrence.type?.value === 'endDate' && (
                  <FormField>
                    <Label>Data final</Label>
                    <DateInput
                      name='recurrence.endDate'
                      placeholder='Data final'
                      value={values.recurrence.endDate}
                      onChange={date => setFieldValue ('recurrence.endDate', date)}
                      onBlur={setFieldTouched}
                      style={{width: '100%'}}
                    />
                  </FormField>
                )}
              </Row>
            )}
            {errorMessage && (
              <ErrorContainer>
                <InfoText>{errorMessage}</InfoText>
              </ErrorContainer>
            )}
            {dirty && (
              <MainButton
                label='Salvar alterações'
                onClick={handleSubmit}
                disabled={isSubmitting}
                loading={isSubmitting}
                width={180}
              />
            )}
          </Form>
        )
      }}
    </Formik>
  )
}
